/**
 * YTD Revenue by Project Drilldown Component
 * Phase 9.38 - Dashboard Stat Drilldowns
 *
 * Shows collected revenue year-to-date broken down by project
 */
import { useYTDRevenueByProject } from '@/hooks/useDashboardReports';
import { AlertCircle } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDateOnly } from '@/lib/utils';

type SortBy = 'amount' | 'recent' | 'client';

export function YTDRevenueByProject() {
  const { data, isLoading, error } = useYTDRevenueByProject();
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState<SortBy>('amount');

  if (isLoading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-4 animate-pulse"></div>
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-100 rounded animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        <AlertCircle className="inline mr-2" size={20} />
        Failed to load YTD revenue data
      </div>
    );
  }

  const projects = data?.data || [];
  const summary = data?.summary || {
    total_collected: 0,
    invoice_count: 0,
    project_count: 0,
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateStr: string | null) => {
    // last_payment_date is a DATE column. Parsing it with the bare Date
    // constructor treats it as UTC midnight, which renders one calendar
    // day early west of UTC (e.g. America/Edmonton). formatDateOnly parses
    // the y/m/d components directly so the displayed date always matches
    // the stored calendar date.
    if (!dateStr) return '-';
    return formatDateOnly(dateStr, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Sort projects
  const sortedProjects = [...projects].sort((a, b) => {
    if (sortBy === 'amount') {
      return b.collected_amount - a.collected_amount;
    } else if (sortBy === 'recent') {
      const dateA = a.last_payment_date ? new Date(a.last_payment_date).getTime() : 0;
      const dateB = b.last_payment_date ? new Date(b.last_payment_date).getTime() : 0;
      return dateB - dateA; // Most recent first
    } else {
      return (a.client_name || '').localeCompare(b.client_name || '');
    }
  });

  const handleRowClick = (projectId: string) => {
    navigate(`/projects/${projectId}?section=invoices`);
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow" data-testid="ytd-revenue-by-project">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">
          YTD Revenue by Project
        </h2>
        <div className="flex gap-2 text-sm">
          <button
            onClick={() => setSortBy('amount')}
            className={`px-3 py-1 rounded ${sortBy === 'amount' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
          >
            Amount
          </button>
          <button
            onClick={() => setSortBy('recent')}
            className={`px-3 py-1 rounded ${sortBy === 'recent' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
          >
            Recent
          </button>
          <button
            onClick={() => setSortBy('client')}
            className={`px-3 py-1 rounded ${sortBy === 'client' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
          >
            Client
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-green-50 p-4 rounded-lg">
          <div className="text-sm text-gray-600">Total Collected</div>
          <div className="text-xl font-bold text-green-600" data-testid="summary-total">
            {formatCurrency(summary.total_collected)}
          </div>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="text-sm text-gray-600">Paid Invoices</div>
          <div className="text-xl font-bold text-gray-900" data-testid="summary-invoices">
            {summary.invoice_count}
          </div>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="text-sm text-gray-600">Projects</div>
          <div className="text-xl font-bold text-gray-900" data-testid="summary-projects">
            {summary.project_count}
          </div>
        </div>
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="text-sm text-gray-600">Avg per Project</div>
          <div className="text-xl font-bold text-sky-600">
            {summary.project_count > 0
              ? formatCurrency(summary.total_collected / summary.project_count)
              : formatCurrency(0)}
          </div>
        </div>
      </div>

      {/* Table */}
      {sortedProjects.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Project</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Collected</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Invoices</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Payment</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedProjects.map((project) => (
                <tr
                  key={project.project_id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleRowClick(project.project_id)}
                >
                  <td className="px-4 py-3 text-sm">
                    <span className="font-medium text-gray-900">
                      {project.project_number ? `#${project.project_number}` : 'Proposal'}
                    </span>
                    <span className="ml-2 text-gray-600">{project.project_name}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {project.client_name || 'No Client'}
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-right text-green-600">
                    {formatCurrency(project.collected_amount)}
                  </td>
                  <td className="px-4 py-3 text-sm text-center text-gray-600">
                    {project.invoice_count}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {formatDate(project.last_payment_date)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          No revenue collected this year
        </div>
      )}
    </div>
  );
}
